import { c as _c } from "react/compiler-runtime";
import type { JSX } from 'react';
import Script from 'next/script';

/**
 * The three bits of the Cookiebot dialog's Extern skin a stylesheet cannot
 * reach: "Show details" moved into the button group, the California link scoped
 * to visitors Cookiebot places under the CCPA, and "Your Privacy Choices" added
 * to the Webflow footer for those visitors (a no-op in these apps, kept so the
 * three copies stay identical). Copied from
 * extern-website's `components/consent/cookiebot-skin-script.tsx`, itself a
 * copy of the `<script>` www.extern.com's Webflow body carries, so every
 * extern.com surface shows the same banner. Edit all or none. The stylesheet
 * half is `cookiebot-skin.css`. Copy and button labels are dashboard fields,
 * not code.
 *
 * `data-cookieconsent="ignore"`: this draws the consent banner itself, so it
 * has to run when no consent exists yet.
 *
 * Hooks `CookiebotOnDialogDisplay` and not `DOMContentLoaded`: Cookiebot defers
 * `DOMContentLoaded` listeners by several seconds. `CookiebotOnDialogInit` is
 * no alternative either, it fires before the template is built. Everything
 * here is idempotent and re-runnable, because Cookiebot rebuilds the dialog
 * from scratch on renew and on closing the Customize panel, and the event
 * fires again.
 *
 * `afterInteractive` rather than `beforeInteractive`: `uc.js` is
 * `beforeInteractive` and hydration waits on it, so by the time this runs the
 * dialog may already be on the page, which is why the skin is applied once on
 * parse as well as on every display event.
 */
export const COOKIEBOT_DIALOG_DISPLAY_EVENT = 'CookiebotOnDialogDisplay';
/** Fired once uc.js has run and `Cookiebot.regulations` exists, dialog or no dialog. */
export const COOKIEBOT_LOAD_EVENT = 'CookiebotOnLoad';
/** Fired on a returning visit once the stored decision is in force; the country is known by then. */
export const COOKIEBOT_CONSENT_READY_EVENT = 'CookiebotOnConsentReady';

/**
 * The three bits of the Cookiebot dialog's Extern skin a stylesheet cannot
 * reach: "Show details" moved into the button group, the title's last word set
 * in serif, and the accept button relabelled. Copied from extern-website's
 * `components/consent/cookiebot-skin-script.tsx`, itself a copy of the
 * `<script>` www.extern.com's Webflow body carries, so every extern.com surface
 * shows the same banner. Edit all or none. The stylesheet half lives in
 * `app/globals.css`.
 *
 * `data-cookieconsent="ignore"`: this draws the consent banner itself, so it
 * has to run when no consent exists yet.
 *
 * Hooks `CookiebotOnDialogDisplay` and not `DOMContentLoaded`: Cookiebot defers
 * `DOMContentLoaded` listeners by several seconds. `CookiebotOnDialogInit` is
 * no alternative either — it fires before the template is built, so the title
 * does not exist yet. Everything here is idempotent and re-runnable, because
 * Cookiebot rebuilds the dialog from scratch on renew and on closing the
 * Customize panel, and the event fires again.
 *
 * `afterInteractive` rather than `beforeInteractive`: `uc.js` is
 * `beforeInteractive` and hydration waits on it, so by the time this runs the
 * dialog may already be on the page — which is why the skin is applied once on
 * parse as well as on every display event.
 */
export function CookiebotSkinScript() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Script data-cookieconsent="ignore" id="extern-cookiebot-skin" strategy="afterInteractive">{`(function () {
  var OUTSIDE_CALIFORNIA_CLASS = 'cb-outside-california';

  // "Show details" (labelled "Manage" in the dashboard) renders inside #...TabContent
  // while the buttons render inside #...Footer -- sibling subtrees under
  // .CybotCookiebotDialogContentWrapper, so flex \`order\` cannot reach across them, and
  // display:contents on the wrappers in between would take the banner's scroll container
  // and the Customize panel down with it. Moving the node drags the tab order along with
  // the visual order; a CSS reorder would have left the two out of step.
  //
  // It goes to #...BodyButtons at every width and stays there -- no restore, so nothing
  // to get stranded. Deliberately not #...BodyButtonsWrapper inside it: that wrapper's
  // children belong to Cookiebot's sortBannerButtons(), which clears it before
  // re-appending the buttons, and a link parked in there is destroyed on the next resize.
  function placeMoreDetails() {
    var moreDetails = document.getElementById('CybotCookiebotDialogBodyEdgeMoreDetails');
    var buttonGroup = document.getElementById('CybotCookiebotDialogBodyButtons');

    if (!moreDetails || !buttonGroup) { return; }
    if (moreDetails.parentElement === buttonGroup) { return; }

    buttonGroup.appendChild(moreDetails);
  }

  // Cookiebot starts every regulation at "applies" and only corrects them once its config
  // resolves the visitor's country (\`userCountry\` goes from "" to e.g. "US-13" in the same
  // step). Reading \`regulations\` before that reports California for everyone, so nothing
  // below acts until the country is known.
  function regimeKnown() {
    var cookiebot = window.Cookiebot;

    return !!cookiebot && typeof cookiebot.userCountry === 'string' && cookiebot.userCountry !== '' && !!cookiebot.regulations;
  }

  // "Do Not Sell or Share My Personal Information" is the link title Cal. Civ. Code
  // 1798.135(a)(1) prescribes, and the dashboard shows it to every visitor. Cookiebot
  // resolves the visitor's regime before it builds the dialog (\`Cookiebot.regulations\`,
  // geo-IP, California for ccpaApplies), so the class goes on when that says the CCPA
  // does not apply and the stylesheet hides the link. Fail open: no regulations object,
  // no class, link stays.
  function scopeDoNotSellLink() {
    var dialog = document.getElementById('CybotCookiebotDialog');

    if (!dialog || !regimeKnown()) { return; }

    var regulations = window.Cookiebot.regulations;

    dialog.classList.toggle(OUTSIDE_CALIFORNIA_CLASS, regulations.ccpaApplies !== true);
  }


  // California visitors also get "Your Privacy Choices" in the site footer, next to
  // "Privacy policy", so the link outlives the banner (11 CCR 7015 wants it in the header
  // or footer of the homepage). The /home experiment renders its own footer in React and
  // adds the link there; this covers the Webflow pages, whose footer carries the class
  // below. Runs on CookiebotOnLoad as well, because a returning visitor never sees the
  // dialog and the display event never fires.
  function placePrivacyChoicesFooterLink() {
    if (!regimeKnown() || window.Cookiebot.regulations.ccpaApplies !== true) { return; }
    if (document.querySelector('.extern-privacy-choices')) { return; }

    var policyLink = document.querySelector('a.footer-rightcol-child-link[href="/privacy-policy"]');

    if (!policyLink) { return; }

    var link = document.createElement('a');

    // classList, not a regex on className: this body is pasted into a template literal in
    // two apps and raw into Webflow, and backslash escapes would differ between them.
    link.className = policyLink.className;
    link.classList.remove('w--current');
    link.classList.add('extern-privacy-choices');
    link.href = '/privacy-policy#your-privacy-choices';
    link.textContent = 'Your Privacy Choices';
    policyLink.insertAdjacentElement('afterend', link);
  }

  function applyExternSkin() {
    // Each in its own try: a throw in one must not skip the other.
    try { placeMoreDetails(); } catch (e) { /* never break the page */ }
    try { scopeDoNotSellLink(); } catch (e) { /* never break the page */ }
    try { placePrivacyChoicesFooterLink(); } catch (e) { /* never break the page */ }
  }

  // The country is known by the display event (first visit) and by consent-ready (returning
  // visitor, who never sees the dialog); load fires after both and is kept as a backstop.
  // Switching tabs does not reset either: sortBannerButtons() re-appends the same nodes
  // rather than re-rendering them, and the class sits on the dialog root. Only a full
  // rebuild loses them, and that fires CookiebotOnDialogDisplay again.
  window.addEventListener('${COOKIEBOT_DIALOG_DISPLAY_EVENT}', applyExternSkin);
  window.addEventListener('${COOKIEBOT_CONSENT_READY_EVENT}', applyExternSkin);
  window.addEventListener('${COOKIEBOT_LOAD_EVENT}', applyExternSkin);
  // In case the dialog is already on the page by the time this parses.
  applyExternSkin();
})();`}</Script>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}